import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useParticipation } from "../../hooks/use-participation";
import {
  isSubmissionWindowActive,
  canSubmitSubmission,
} from "../../utils/campaign-utils";
import { SkeletonWave } from "../../../../components/common/skeleton-wave";
import BACK_IMG from "../../../../components/common/back-img.png";
import {
  useCancelDepositCharge,
  useCancelParticipation,
} from "../../hooks/use-campaign-payment";
import { notify, notifyError } from "../../../../utils/toast";

const ParticipationDetailPage = () => {
  const { participationId } = useParams<{ participationId: string }>();
  const navigate = useNavigate();
  const participationIdNum = participationId ? parseInt(participationId) : 0;
  const { participation, loading, error, refetch } =
    useParticipation(participationIdNum);
  const { cancelDepositCharge, loading: cancelDepositChargeLoading } =
    useCancelDepositCharge();
  const { cancelParticipation, loading: cancelParticipationLoading } =
    useCancelParticipation();
  const cancelLoading =
    cancelDepositChargeLoading || cancelParticipationLoading;
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Record<number, "example" | "my">>(
    {}
  );

  const handleBackClick = () => {
    const idx = (window.history.state && window.history.state?.idx) ?? 0;
    idx > 0 ? navigate(-1) : navigate("/my-challenges");
  };

  const handleSubmitClick = (submission: any) => {
    navigate(
      `/campaigns/submission/${submission.id}?participationId=${participationIdNum}`
    );
  };

  const handleContactClick = () => {
    window.open("http://pf.kakao.com/_xawxcRn/chat", "_blank");
  };

  const handleDetailPageClick = () => {
    if (campaign?.id) {
      navigate(`/campaigns/${campaign.id}`);
    }
  };

  // 날짜 포맷팅 함수들
  const formatStartTime = (dateTime: string | Date): string => {
    const date = new Date(dateTime);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const ampm = hour < 12 ? "오전" : "오후";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${month}월 ${day}일 ${ampm} ${displayHour}시에 시작!`;
  };

  // 인증 가능 시간 포맷팅 함수 (구매 시간과 동일한 포맷)
  const formatWindowTime = (
    startAt: string | Date,
    endAt: string | Date
  ): string => {
    const startDate = new Date(startAt);
    const endDate = new Date(endAt);

    const getMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    const getAmPmHour = (d: Date) => {
      const h = d.getHours();
      const ampm = h < 12 ? "오전" : "오후";
      const hour = h % 12 || 12; // 0시는 12시로 표시
      return `${ampm} ${hour}시`;
    };

    // 시작일과 종료일이 모두 00시인 경우
    const isStartMidnight =
      startDate.getHours() === 0 && startDate.getMinutes() === 0;
    const isEndMidnight =
      endDate.getHours() === 0 && endDate.getMinutes() === 0;
    const timeDiff = endDate.getTime() - startDate.getTime();
    const oneDayInMs = 86400000; // 24시간

    if (isStartMidnight && isEndMidnight) {
      // 정확히 24시간 (1일)인 경우: "1/16일 하루동안"
      if (timeDiff === oneDayInMs) {
        return `${getMonthDay(startDate)}일 하루동안`;
      }
      // 1일 이상 차이인 경우: 종료일을 하루 빼서 표기 (예: 1/16 00시 ~ 1/18 00시 → 1/16~1/17)
      const displayEndDate = new Date(endDate);
      displayEndDate.setDate(displayEndDate.getDate() - 1);
      return `${getMonthDay(startDate)}~${getMonthDay(displayEndDate)}`;
    }

    const isSameDay =
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth();

    const startStr = `${getMonthDay(startDate)} ${getAmPmHour(startDate)}`;

    if (isSameDay) {
      return `${startStr} ~ ${getAmPmHour(endDate)}`;
    } else {
      return `${startStr} ~ ${getMonthDay(endDate)} ${getAmPmHour(endDate)}`;
    }
  };

  // Helper to format date range for "Purchase Time" with AM/PM
  const getFormattedRunTime = (run: any) => {
    if (!run) return "";
    const startDate = new Date(run.startAt);
    const endDate = new Date(run.endAt);

    const getMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    const getAmPmHour = (d: Date) => {
      const h = d.getHours();
      const ampm = h < 12 ? "오전" : "오후";
      const hour = h % 12 || 12; // 0시는 12시로 표시
      return `${ampm} ${hour}시`;
    };

    // 시작일과 종료일이 모두 00시인 경우
    const isStartMidnight =
      startDate.getHours() === 0 && startDate.getMinutes() === 0;
    const isEndMidnight =
      endDate.getHours() === 0 && endDate.getMinutes() === 0;
    const timeDiff = endDate.getTime() - startDate.getTime();
    const oneDayInMs = 86400000; // 24시간

    if (isStartMidnight && isEndMidnight) {
      // 정확히 24시간 (1일)인 경우: "1/16일 하루동안"
      if (timeDiff === oneDayInMs) {
        return `${getMonthDay(startDate)}일 하루동안`;
      }
      // 1일 이상 차이인 경우: 종료일을 하루 빼서 표기 (예: 1/16 00시 ~ 1/18 00시 → 1/16~1/17)
      const displayEndDate = new Date(endDate);
      displayEndDate.setDate(displayEndDate.getDate() - 1);
      return `${getMonthDay(startDate)}~${getMonthDay(displayEndDate)}`;
    }

    const isSameDay =
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth();

    const startStr = `${getMonthDay(startDate)} ${getAmPmHour(startDate)}`;

    if (isSameDay) {
      return `${startStr} ~ ${getAmPmHour(endDate)}`;
    } else {
      return `${startStr} ~ ${getMonthDay(endDate)} ${getAmPmHour(endDate)}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  if (error || !participation) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              {error
                ? "데이터를 불러오는 중 오류가 발생했습니다."
                : "참여 정보를 찾을 수 없습니다."}
            </p>
            <button
              onClick={handleBackClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const campaign = participation.run?.campaign;
  const submissions = participation.submissions || [];
  const completedCount = submissions.filter(
    (s: any) => s.status === "APPROVED"
  ).length;
  const totalCount = submissions.length;

  // 다음 제출 가능한 submission 찾기
  const nextAvailableSubmission = submissions.find((s: any) =>
    canSubmitSubmission(s, submissions)
  );

  // 캠페인 시작 여부 확인 (windowStartAt 기준)
  const isCampaignStarted = nextAvailableSubmission
    ? new Date(nextAvailableSubmission.windowStartAt) <= new Date()
    : true;

  // 첫 번째 submission의 시작 시간 가져오기
  const firstSubmission = submissions.length > 0 ? submissions[0] : null;
  const startTime =
    firstSubmission?.windowStartAt || participation.run?.startAt;

  // 취소 가능 여부 확인 (depositChargeId가 있거나 없거나 상관없이 JOINED 또는 IN_PROGRESS 상태면 취소 가능)
  const canCancel =
    participation.status === "JOINED" || participation.status === "IN_PROGRESS";

  // 캠페인 시작 2일 전까지 취소 가능
  const startAt = participation.run?.startAt
    ? new Date(participation.run.startAt)
    : null;
  const twoDaysBeforeStart = startAt
    ? new Date(startAt.getTime() - 2 * 24 * 60 * 60 * 1000)
    : null;
  const isWithinCancelWindow =
    twoDaysBeforeStart && new Date() <= twoDaysBeforeStart;

  const handleCancelClick = async () => {
    if (
      !window.confirm(
        participation.depositChargeId
          ? "캠페인 참여를 취소하시겠습니까? 예치금이 환불됩니다."
          : "캠페인 참여를 취소하시겠습니까?"
      )
    ) {
      return;
    }

    try {
      // depositChargeId가 있으면 cancelDepositCharge, 없으면 cancelParticipation 호출
      if (participation.depositChargeId) {
        const { data } = await cancelDepositCharge({
          variables: {
            depositChargeId: participation.depositChargeId,
            cancelReason: "사용자 요청",
          },
        });

        if (data?.cancelDepositCharge.ok) {
          notify("캠페인 참여가 취소되었습니다. 예치금이 환불됩니다.");
          refetch();
          setTimeout(() => {
            navigate("/my-challenges");
          }, 1500);
        } else {
          notifyError(
            data?.cancelDepositCharge.error || "취소에 실패했습니다."
          );
        }
      } else {
        const { data } = await cancelParticipation({
          variables: {
            participationId: participation.id,
            cancelReason: "사용자 요청",
          },
        });

        if (data?.cancelParticipation.ok) {
          notify("캠페인 참여가 취소되었습니다.");
          refetch();
          setTimeout(() => {
            navigate("/my-challenges");
          }, 1500);
        } else {
          notifyError(
            data?.cancelParticipation.error || "취소에 실패했습니다."
          );
        }
      }
    } catch (error: any) {
      notifyError(error.message || "취소 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto pb-24">
        {/* 제품 이미지 섹션 */}
        {campaign?.productImageUrl && (
          <div className="relative w-full h-[60vh] mb-4">
            {/* 배경 이미지 */}
            <img
              src={campaign.productImageUrl}
              alt={campaign.productName}
              className="w-full h-full object-cover"
            />
            {/* 오버레이 그라데이션 */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40" />

            {/* 뒤로가기 버튼 */}
            <button
              onClick={handleBackClick}
              className="absolute top-4 left-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg z-10"
            >
              <img src={BACK_IMG} alt="back" className="w-6 h-6" />
            </button>

            {/* 제품명 텍스트 */}
            <div className="absolute bottom-24 left-4 right-4">
              <h1 className="text-white text-xl font-bold drop-shadow-lg">
                {campaign.productName}
              </h1>
            </div>

            {/* 상세 페이지로 이동 버튼 */}
            <button
              onClick={handleDetailPageClick}
              className="absolute bottom-16 left-4 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium shadow-lg hover:bg-gray-50 transition-colors"
            >
              상세 페이지로 이동 →
            </button>

            {/* 예치금/페이백 안내 */}
            <div className="absolute bottom-16 right-4 flex items-center gap-1 text-white text-sm drop-shadow-lg">
              <span>예치금/페이백 안내</span>
              <span className="text-xs">ⓘ</span>
            </div>
          </div>
        )}

        {/* 요약 섹션 */}
        <div className="bg-white px-4 pb-4 mb-4">
          {/* 시작 시간 */}
          {startTime && (
            <div className="mb-4">
              <h2 className="text-xl font-bold text-black">
                {formatStartTime(startTime)}
              </h2>
            </div>
          )}

          {/* 인증 단계 */}
          {submissions.length > 0 && (
            <div className="mb-10 w-full relative">
              {/* 회색 연결선 (배경) */}
              <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-200 -z-0" />

              <div className="flex justify-between w-full relative z-10">
                {submissions.map((submission: any, index: number) => {
                  const isCompleted = submission.status === "APPROVED";

                  return (
                    <div
                      key={submission.id}
                      className="flex flex-col items-center flex-1"
                    >
                      {/* 스텝 번호 (둥근 사각형) */}
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg text-gray-500 font-medium mb-3">
                        {submission.stepOrder}
                      </div>

                      {/* 스텝 라벨 (높이 고정으로 정렬 맞춤) */}
                      <div className="h-10 text-sm text-black font-medium text-center mb-2 px-1 break-keep w-full flex items-start justify-center">
                        <span className="line-clamp-2">
                          {submission.step?.title ||
                            `미션 ${submission.stepOrder}`}
                        </span>
                      </div>

                      {/* 인증 상태 배지 (알약 모양) */}
                      <div className="px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-500 font-medium">
                        {isCompleted ? "인증완료" : "미인증"}
                      </div>
                    </div>
                  );
                })}

                {/* 페이백 단계 */}
                <div className="flex flex-col items-center flex-1">
                  {/* 자물쇠 아이콘 (주황색, 둥근 사각형) */}
                  <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center mb-3 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-9h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zM9 6a3 3 0 1 1 6 0v2H9V6z" />
                    </svg>
                  </div>

                  {/* 페이백 금액 (높이 고정으로 정렬 맞춤) */}
                  <div className="h-10 text-sm text-black font-medium text-center mb-2 flex flex-col items-center justify-start">
                    <span>{participation.prizeAmount.toLocaleString()}원</span>
                    <span className="flex items-center gap-1">
                      환급{" "}
                      <span className="text-gray-400 text-xs border border-gray-400 rounded-full w-3.5 h-3.5 flex items-center justify-center">
                        i
                      </span>
                    </span>
                  </div>

                  {/* 페이백 상태 배지 */}
                  <div className="px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-500 font-medium">
                    검토 후 지급
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 중요 안내 */}
          <div className="mb-4 flex items-center gap-2 text-blue-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium">
              제품 구매 시간을 지켜야 성공으로 처리돼요!
            </span>
          </div>

          {/* 구매 시간 */}
          {participation.run && (
            <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center mt-6">
              <span className="font-bold text-gray-800">구매 시간</span>
              <span className="font-bold text-gray-900">
                {getFormattedRunTime(participation.run)}
              </span>
            </div>
          )}
        </div>

        {/* 미션 스텝 리스트 */}
        <div className="bg-white px-4 pb-4 mb-4">
          <div className="space-y-6">
            {submissions.map((submission: any, index: number) => {
              const canSubmit = canSubmitSubmission(submission, submissions);
              const isWindowActive = isSubmissionWindowActive(
                submission.windowStartAt,
                submission.windowEndAt
              );
              const step = submission.step;

              // 제출한 이미지 URL 추출
              const extractImageUrls = (proofData: unknown): string[] => {
                if (!proofData) return [];

                // 문자열인 경우 JSON 파싱 시도
                if (typeof proofData === "string") {
                  // 이미 URL인 경우
                  if (
                    proofData.startsWith("http://") ||
                    proofData.startsWith("https://")
                  ) {
                    return [proofData];
                  }
                  // JSON 문자열인 경우 파싱
                  try {
                    const parsed = JSON.parse(proofData);
                    return extractImageUrls(parsed);
                  } catch {
                    // 파싱 실패 시 그대로 반환 (이미지 URL일 수 있음)
                    return proofData ? [proofData] : [];
                  }
                }

                // 배열인 경우
                if (Array.isArray(proofData)) {
                  const urls: string[] = [];
                  proofData.forEach((item) => {
                    if (typeof item === "string") {
                      urls.push(item);
                    } else if (
                      item &&
                      typeof item === "object" &&
                      item !== null
                    ) {
                      const itemObj = item as Record<string, unknown>;
                      // imageUrl 필드 확인
                      if (typeof itemObj.imageUrl === "string") {
                        urls.push(itemObj.imageUrl);
                      }
                      // url 필드 확인
                      if (typeof itemObj.url === "string") {
                        urls.push(itemObj.url);
                      }
                      // 직접 URL이 객체에 있는 경우
                      if (typeof itemObj === "string") {
                        urls.push(itemObj as string);
                      }
                    }
                  });
                  return urls;
                }

                // 객체인 경우
                if (
                  proofData &&
                  typeof proofData === "object" &&
                  proofData !== null
                ) {
                  const obj = proofData as Record<string, unknown>;
                  const urls: string[] = [];

                  // imageUrl 필드 확인
                  if (typeof obj.imageUrl === "string") {
                    urls.push(obj.imageUrl);
                  }
                  // url 필드 확인
                  if (typeof obj.url === "string") {
                    urls.push(obj.url);
                  }
                  // images 배열 확인
                  if (Array.isArray(obj.images)) {
                    obj.images.forEach((img: unknown) => {
                      if (typeof img === "string") {
                        urls.push(img);
                      } else if (img && typeof img === "object") {
                        const imgObj = img as Record<string, unknown>;
                        if (typeof imgObj.url === "string") {
                          urls.push(imgObj.url);
                        }
                        if (typeof imgObj.imageUrl === "string") {
                          urls.push(imgObj.imageUrl);
                        }
                      }
                    });
                  }

                  return urls.length > 0 ? urls : [];
                }

                return [];
              };

              // 나의 인증 이미지 추출 - 조건 완화
              const submittedImages = submission.proofData
                ? extractImageUrls(submission.proofData)
                : [];

              // 제출한 이미지가 있으면 기본적으로 "나의 인증" 탭 선택
              const defaultTab = submittedImages.length > 0 ? "my" : "example";
              const currentTab = activeTab[submission.id] || defaultTab;

              return (
                <div key={submission.id} className="mb-6">
                  {/* 스텝 헤더 */}
                  <div className="mb-4">
                    <div className="flex items-start gap-3 mb-2">
                      {/* 스텝 번호 뱃지 */}
                      <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                        {submission.stepOrder}
                      </div>
                      {/* 스텝 제목 */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-black mb-1">
                          {step?.title || `미션 ${submission.stepOrder}`}
                        </h3>
                        {/* 지침 텍스트 */}
                        {step?.guide &&
                          Array.isArray(step.guide) &&
                          step.guide.length > 0 && (
                            <p className="text-sm text-black">
                              {step.guide[0]}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* 콘텐츠 영역 */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* 좌측: 인증샷 예시 */}
                    <div className="flex flex-col">
                      {/* 레이블 - 중앙 정렬 */}
                      <div className="flex justify-center mb-2">
                        <button
                          onClick={() =>
                            setActiveTab({
                              ...activeTab,
                              [submission.id]: "example",
                            })
                          }
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            currentTab === "example"
                              ? "bg-gray-100 text-black"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          인증샷 예시
                        </button>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        {/* 제품 이미지 */}
                        <div
                          className="mb-4 aspect-square w-full bg-gray-100 rounded-lg overflow-hidden cursor-pointer relative"
                          onClick={() => handleSubmitClick(submission)}
                        >
                          {step?.imageUrl ? (
                            <>
                              <img
                                src={step.imageUrl}
                                alt={
                                  step?.title ||
                                  `미션 ${submission.stepOrder} 가이드`
                                }
                                className="w-full h-full object-cover"
                              />
                              {/* 전체화면 아이콘 */}
                              <div className="absolute bottom-2 right-2 w-8 h-8 bg-black rounded flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                  />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gray-400">이미지 없음</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 우측: 나의 인증 및 인증 가능 시간 */}
                    <div className="flex flex-col">
                      {/* 레이블 - 중앙 정렬 */}
                      <div className="flex justify-center mb-2">
                        <button
                          onClick={() =>
                            setActiveTab({
                              ...activeTab,
                              [submission.id]: "my",
                            })
                          }
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            currentTab === "my"
                              ? "bg-gray-100 text-black"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          나의 인증
                        </button>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        {currentTab === "my" ? (
                          <>
                            {/* 나의 인증 이미지 */}
                            <div className="mb-4 aspect-square w-full bg-gray-100 rounded-lg overflow-hidden relative">
                              {submittedImages.length > 0 ? (
                                <>
                                  <div
                                    className="relative group cursor-pointer w-full h-full"
                                    onClick={() =>
                                      setSelectedImage(submittedImages[0])
                                    }
                                  >
                                    <img
                                      src={submittedImages[0]}
                                      alt={`제출 이미지 1`}
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                        />
                                      </svg>
                                    </div>
                                    {/* 전체화면 아이콘 */}
                                    <div className="absolute bottom-2 right-2 w-8 h-8 bg-black rounded flex items-center justify-center z-10">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                        />
                                      </svg>
                                    </div>
                                  </div>
                                  {/* 인증 상태 칩 - 중앙에 약간 투명하게 */}
                                  {submission.status === "APPROVED" && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="bg-green-500 bg-opacity-90 text-white px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        인증 성공
                                      </div>
                                    </div>
                                  )}
                                  {submission.status === "SUBMITTED" && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="bg-yellow-500 bg-opacity-90 text-white px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                        검토 중
                                      </div>
                                    </div>
                                  )}
                                  {submission.status === "REJECTED" && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSubmitClick(submission);
                                        }}
                                        className="bg-red-500 bg-opacity-90 text-white px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 hover:bg-opacity-100 transition-opacity cursor-pointer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                          />
                                        </svg>
                                        재시도
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <span className="text-gray-400">
                                    인증 이미지가 없습니다
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* 실패 사유 */}
                            {submission.status === "REJECTED" &&
                              submission.note && (
                                <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                                  실패 사유: {submission.note}
                                </div>
                              )}
                          </>
                        ) : (
                          <>
                            {/* 인증 가능 시간 */}
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 text-gray-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="text-sm text-black font-medium">
                                  인증 가능 시간
                                </span>
                              </div>
                              <p className="text-lg font-bold text-black">
                                {formatWindowTime(
                                  submission.windowStartAt,
                                  submission.windowEndAt
                                )}
                              </p>
                            </div>
                          </>
                        )}

                        {/* 인증 버튼 - 제출 가능할 때만 표시 */}
                        {canSubmit && isWindowActive && (
                          <div className="mt-4">
                            <button
                              onClick={() => handleSubmitClick(submission)}
                              className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
                                submission.status === "REJECTED"
                                  ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-blue-500 text-white hover:bg-blue-600"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              {submission.status === "REJECTED"
                                ? "다시 인증하기"
                                : "인증하기"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 이미지 확대 모달 */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] w-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-all z-10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={selectedImage}
                alt="확대 이미지"
                className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {/* 하단 액션 영역 */}
        <div className="bg-white rounded-lg shadow-md p-6 sticky bottom-0">
          {/* 취소 가능한 경우 취소 버튼 표시 */}
          {canCancel && isWithinCancelWindow && (
            <div className="mb-3">
              <button
                onClick={handleCancelClick}
                disabled={cancelLoading}
                className="w-full py-3 bg-red-50 border border-red-300 text-red-600 font-semibold rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLoading ? "처리 중..." : "참여 취소하기"}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                캠페인 시작 2일 전까지 취소 가능합니다
              </p>
            </div>
          )}
          {canCancel && !isWithinCancelWindow && (
            <div className="mb-3">
              <button
                disabled
                className="w-full py-3 bg-gray-100 border border-gray-300 text-gray-400 font-semibold rounded-lg cursor-not-allowed"
              >
                취소 불가 (캠페인 시작 2일 전까지만 취소 가능)
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleContactClick}
              className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
            >
              문의하기
            </button>
            <button
              onClick={() =>
                nextAvailableSubmission &&
                handleSubmitClick(nextAvailableSubmission)
              }
              disabled={
                !isCampaignStarted ||
                !nextAvailableSubmission ||
                participation.status === "COMPLETED" ||
                participation.status === "CANCELED"
              }
              className={`flex-1 py-3 font-semibold rounded-lg ${
                !isCampaignStarted ||
                !nextAvailableSubmission ||
                participation.status === "COMPLETED" ||
                participation.status === "CANCELED"
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {participation.status === "CANCELED"
                ? "취소됨"
                : completedCount === totalCount
                ? `인증완료 (${completedCount}/${totalCount})`
                : `인증하기 (${completedCount}/${totalCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipationDetailPage;
